/**
 * Pallet fee (Pallegebyr) — task 57610 / 57096.
 *
 * Keeps a single "Pallegebyr" line item synced to qty = TOTAL PALLETS across all pallet
 * products in the cart, where pallets for a line = ceil(quantity / min_order_qty).
 * Self-heals on every cart change (and on load), so the fee always matches the cart.
 *
 * The pallet size (min_order_qty) is surfaced as `data-pallet-min` on each cart line row
 * (the Ajax Cart `/cart.js` payload carries no product metafields). The Pallegebyr variant
 * id comes from `window.PALLET_FEE_CONFIG` (rendered by snippets/pallet-fee.liquid).
 *
 * Refresh: we re-render the cart sections directly via the section renderer rather than
 * dispatching a `cart:update` event. A `cart:update` would re-trigger the cart drawer's
 * auto-open (CartAddEvent shares the `cart:update` name), racing its showModal() and
 * spuriously opening the drawer on the cart page. A short debounce keeps our re-render out
 * of the drawer's open-animation frame.
 *
 * Loop-safe: our mutations are plain fetches (no theme events), and we only mutate when the
 * current Pallegebyr quantity differs from the desired total.
 */
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const feeVariantId = Number((window.PALLET_FEE_CONFIG || {}).pallegebyrVariantId);

const routes = (window.Theme && Theme.routes) || {};
const CART_URL = (routes.cart_url || '/cart') + '.js';
const CHANGE_URL = routes.cart_change_url || '/cart/change.js';
const ADD_URL = routes.cart_add_url || '/cart/add.js';

let syncing = false;
let rerun = false;

/** @returns {Record<string, number>} cart line-item key -> pallet size (min_order_qty) */
function palletMinByKey() {
  /** @type {Record<string, number>} */
  const map = {};
  document.querySelectorAll('[data-key][data-pallet-min]').forEach((el) => {
    const key = el.getAttribute('data-key');
    const min = parseInt(el.getAttribute('data-pallet-min') || '', 10);
    if (key && min > 0) map[key] = min;
  });
  return map;
}

async function getCart() {
  const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
  return res.json();
}

async function post(url, body) {
  await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Re-render every cart section on the page (drawer + cart page) without a cart:update. */
function refreshCartSections() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    ids.add(el.getAttribute('data-section-id'));
  });
  ids.forEach((id) => sectionRenderer.renderSection(id, { cache: false }));
}

async function sync() {
  if (!feeVariantId) return;
  if (syncing) {
    rerun = true; // a change came in mid-sync — run once more after
    return;
  }
  syncing = true;
  try {
    const cart = await getCart();
    const mins = palletMinByKey();

    let pallets = 0;
    let feeLine = null;
    for (const item of cart.items || []) {
      if (item.variant_id === feeVariantId) {
        feeLine = item;
        continue;
      }
      const min = mins[item.key];
      if (min) pallets += Math.ceil(item.quantity / min);
    }

    const current = feeLine ? feeLine.quantity : 0;
    if (current === pallets) return; // already correct — nothing to do (loop guard)

    if (feeLine) {
      await post(CHANGE_URL, { id: feeLine.key, quantity: pallets }); // quantity 0 removes it
    } else if (pallets > 0) {
      await post(ADD_URL, { id: feeVariantId, quantity: pallets });
    }

    refreshCartSections();
  } catch (error) {
    console.error('[pallet-fee] sync failed', error);
  } finally {
    syncing = false;
    if (rerun) {
      rerun = false;
      sync();
    }
  }
}

let debounceId = null;
function scheduleSync() {
  if (debounceId) clearTimeout(debounceId);
  // Let the native cart render + any drawer open-animation settle before we reconcile.
  debounceId = setTimeout(sync, 250);
}

document.addEventListener(ThemeEvents.cartUpdate, scheduleSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sync);
} else {
  sync();
}
